import React from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Users, TrendingUp, FileText } from 'lucide-react';

const SubscriptionMetricsCard = ({ metrics, isLoading }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="h-16 bg-gray-200 rounded"></div>
            <div className="h-16 bg-gray-200 rounded"></div>
          </div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {t('subscriptionMetrics')}
        </h3>
        <div className="text-center py-8">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 mt-2">{t('noSubscriptionData')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {t('subscriptionMetrics')}
      </h3>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <Users className="w-6 h-6 text-blue-600 mr-3 rtl:ml-3 rtl:mr-0" />
            <p className="text-2xl font-bold text-blue-600">{metrics.totalActiveSubscriptions}</p>
          </div>
          <p className="text-sm text-gray-600">{t('dashboard.activeSubscriptions')}</p>
        </div>
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <TrendingUp className="w-6 h-6 text-green-600 mr-3 rtl:ml-3 rtl:mr-0" />
            <p className="text-2xl font-bold text-green-600">{metrics.newSubscriptionsToday}</p>
          </div>
          <p className="text-sm text-gray-600">{t('newToday')}</p>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">{t('newThisWeek')}</span>
          <span className="font-semibold">{metrics.newSubscriptionsThisWeek}</span>
        </div>
      </div>

      {metrics.subscriptionsByName && metrics.subscriptionsByName.length > 0 && (
        <div>
          <div className="flex items-center mb-3">
            <FileText className="w-5 h-5 text-gray-700 mr-3 rtl:ml-3 rtl:mr-0" />
            <h4 className="text-sm font-medium text-gray-700">{t('subscriptionTypes')}</h4>
          </div>
          <div className="space-y-2">
            {metrics.subscriptionsByName.map((subscription, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{subscription.name}</span>
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <span className="text-sm font-semibold">{subscription.count}</span>
                  <span className="text-xs text-gray-400">
                    ({subscription.percentage?.toFixed(1)}%)
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionMetricsCard;
